import { Box } from '@mantine/core';
import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import type { ReactNode } from 'react';

export interface SplitPaneHandle {
  splitPane: HTMLDivElement | null;
  getMinWidth: () => number | undefined;
  getMaxWidth: () => number | undefined;
}

function getSizeInPx(size: number | string | undefined, parentWidth: number): number | undefined {
  if (size == null) return undefined;
  if (typeof size === 'number') return size;
  const s = String(size);
  if (s.endsWith('px')) {
    const value = Number.parseFloat(s);
    return Number.isNaN(value) ? undefined : value;
  }
  if (s.endsWith('%')) {
    const value = Number.parseFloat(s);
    return Number.isNaN(value) ? undefined : (parentWidth * value) / 100;
  }
  return undefined;
}

function toWidthStyle(value: number | string | undefined): string | undefined {
  if (value == null) return undefined;
  if (typeof value === 'number') return `${value}px`;
  const s = String(value).trim();
  if (s.endsWith('px') || s.endsWith('%')) return s;
  const n = Number.parseFloat(s);
  return Number.isNaN(n) ? undefined : `${n}px`;
}

function applyPaneWidth(
  el: HTMLDivElement,
  parentWidth: number,
  initialWidth: number | string | undefined,
  minWidth: number | string | undefined,
  maxWidth: number | string | undefined
): void {
  const min = toWidthStyle(minWidth);
  if (min != null) el.style.minWidth = min;
  const max = toWidthStyle(maxWidth);
  if (max != null) el.style.maxWidth = max;
  const s = initialWidth != null ? String(initialWidth).trim() : '';
  if (s.endsWith('%')) {
    const pct = Number.parseFloat(s);
    if (!Number.isNaN(pct) && parentWidth > 0) {
      el.style.width = `${(parentWidth * pct) / 100}px`;
    }
  } else {
    const width = toWidthStyle(initialWidth);
    if (width != null) el.style.width = width;
  }
}

export interface SplitPaneProps {
  children?: ReactNode;
  initialWidth?: number | string;
  minWidth?: number | string;
  maxWidth?: number | string;
  grow?: boolean;
}

/**
 * Панель сплита. Ref API: splitPane, getMinWidth, getMaxWidth (как в @gfazioli/mantine-split-pane).
 * Размер задаётся initialWidth; ресайзер выставляет style.width при перетаскивании.
 */
export const SplitPane = forwardRef<SplitPaneHandle, SplitPaneProps>(function SplitPane(
  { children, initialWidth, minWidth, maxWidth, grow: _grow },
  ref
) {
  const domRef = useRef<HTMLDivElement | null>(null);

  useImperativeHandle(
    ref,
    () => ({
      get splitPane() {
        return domRef.current;
      },
      getMinWidth: () => {
        const el = domRef.current?.parentElement;
        if (!el) return undefined;
        const parentWidth = el.getBoundingClientRect().width;
        return getSizeInPx(minWidth, parentWidth);
      },
      getMaxWidth: () => {
        const el = domRef.current?.parentElement;
        if (!el) return undefined;
        const parentWidth = el.getBoundingClientRect().width;
        return getSizeInPx(maxWidth, parentWidth);
      },
    }),
    [minWidth, maxWidth]
  );

  useEffect(() => {
    const el = domRef.current;
    const parent = el?.parentElement;
    if (!el || !parent) return;

    const apply = () => {
      const w = parent.getBoundingClientRect().width;
      applyPaneWidth(el, w, initialWidth, minWidth, maxWidth);
    };

    apply();

    const observer = new ResizeObserver(() => {
      apply();
    });
    observer.observe(parent);

    return () => {
      observer.disconnect();
    };
  }, [initialWidth, minWidth, maxWidth]);

  return (
    <Box ref={domRef} mih={0} h='100%' style={{ overflow: 'hidden', flex: '0 1 auto' }}>
      {children}
    </Box>
  );
});
